/**
 * ReimbursementLinkModal
 *
 * Shown when a user sets a transaction's category to "reimbursement".
 * Allows searching all historical transactions (revolver + cash) and
 * linking the current reimbursement transaction to a primary (non-reimbursement) transaction.
 *
 * Props:
 *   isOpen          – boolean
 *   transaction     – the reimbursement transaction object (must have id, type: 'revolver'|'cash')
 *   onClose         – () => void
 *   onSaveSuccess   – (groupData) => void  called after successful link
 *   formatCurrency  – (number) => string
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

const BASE_URL = '/balance_sheet/financing/revolvers/';

export default function ReimbursementLinkModal({
    isOpen,
    transaction,
    onClose,
    onSaveSuccess,
    formatCurrency,
}) {
    const [query, setQuery] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [selectedTxn, setSelectedTxn] = useState(null);
    const debounceRef = useRef(null);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setQuery('');
            setDateFrom('');
            setDateTo('');
            setResults([]);
            setError('');
            setSelectedTxn(null);
        }
    }, [isOpen]);

    const doSearch = useCallback(async (q, df, dt) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (q) params.set('q', q);
            if (df) params.set('date_from', df);
            if (dt) params.set('date_to', dt);
            params.set('limit', '100');
            const resp = await api.get(`${BASE_URL}transaction-search/?${params.toString()}`);
            setResults(resp.data || []);
        } catch (e) {
            setError('Search failed. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Auto-search on mount (show recent transactions)
    useEffect(() => {
        if (isOpen) {
            doSearch('', '', '');
        }
    }, [isOpen, doSearch]);

    const handleQueryChange = (e) => {
        const v = e.target.value;
        setQuery(v);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(v, dateFrom, dateTo), 400);
    };

    const handleDateChange = (field, val) => {
        const df = field === 'from' ? val : dateFrom;
        const dt = field === 'to' ? val : dateTo;
        if (field === 'from') setDateFrom(val);
        else setDateTo(val);
        doSearch(query, df, dt);
    };

    const handleLink = async () => {
        if (!selectedTxn) {
            setError('Please select a transaction to link to.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = {
                primary_type: selectedTxn.type,
                primary_id: selectedTxn.id,
                reimbursement_type: transaction.type || 'revolver',
                reimbursement_id: transaction.id,
            };
            const resp = await api.post(`${BASE_URL}reimbursement-groups/`, payload);
            onSaveSuccess(resp.data.group);
        } catch (e) {
            const msg = e?.response?.data?.detail || 'Failed to link reimbursement.';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !transaction) return null;

    const fmt = formatCurrency || ((v) => `$${Number(v).toFixed(2)}`);

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2200 }}
            onClick={() => { if (!saving) onClose(); }}
        >
            <div
                style={{ width: 'min(820px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: '#0b1220', border: '1px solid #374151', borderRadius: 8, color: '#e5e7eb' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 18, fontWeight: 700 }}>🔗 Link Reimbursement</span>
                    <span style={{ color: '#9ca3af', fontSize: 13 }}>
                        Select the original transaction that was reimbursed
                    </span>
                    <button className="btn btn-sm btn-secondary" style={{ marginLeft: 'auto' }} onClick={() => { if (!saving) onClose(); }}>✕</button>
                </div>

                {/* Reimbursement txn info */}
                <div style={{ padding: '10px 16px', background: '#0f1e35', borderBottom: '1px solid #1f2937', fontSize: 13 }}>
                    <strong>Reimbursement:</strong>&nbsp;
                    {transaction.description || transaction.user_description || '—'}&nbsp;
                    <span style={{ color: '#34d399' }}>{fmt(Math.abs(Number(transaction.amount) || 0))}</span>&nbsp;
                    <span style={{ color: '#9ca3af' }}>{transaction.transaction_date || transaction.date || ''}</span>
                </div>

                {/* Search controls */}
                <div style={{ padding: '10px 16px', borderBottom: '1px solid #1f2937', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search description…"
                        value={query}
                        onChange={handleQueryChange}
                        style={{ flex: '1 1 200px', minWidth: 160, background: '#111827', color: '#e5e7eb', border: '1px solid #374151' }}
                    />
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={dateFrom}
                        onChange={(e) => handleDateChange('from', e.target.value)}
                        style={{ width: 140, background: '#111827', color: '#e5e7eb', border: '1px solid #374151' }}
                        title="From date"
                    />
                    <span style={{ color: '#6b7280' }}>–</span>
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={dateTo}
                        onChange={(e) => handleDateChange('to', e.target.value)}
                        style={{ width: 140, background: '#111827', color: '#e5e7eb', border: '1px solid #374151' }}
                        title="To date"
                    />
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => doSearch(query, dateFrom, dateTo)} disabled={loading}>
                        {loading ? '…' : '🔍'}
                    </button>
                </div>

                {/* Results table */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 4px 0' }}>
                    {error && (
                        <div style={{ padding: '8px 16px', color: '#f87171', fontSize: 13 }}>{error}</div>
                    )}
                    {loading && (
                        <div style={{ padding: '16px', color: '#9ca3af', textAlign: 'center' }}>Searching…</div>
                    )}
                    {!loading && results.length === 0 && (
                        <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center' }}>No transactions found.</div>
                    )}
                    {!loading && results.length > 0 && (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#111827', position: 'sticky', top: 0 }}>
                                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#9ca3af', fontWeight: 600 }}>Select</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#9ca3af', fontWeight: 600 }}>Date</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#9ca3af', fontWeight: 600 }}>Description</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right', color: '#9ca3af', fontWeight: 600 }}>Amount</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#9ca3af', fontWeight: 600 }}>Account</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'left', color: '#9ca3af', fontWeight: 600 }}>Category</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((r) => {
                                    const isSelected = selectedTxn && selectedTxn.id === r.id && selectedTxn.type === r.type;
                                    return (
                                        <tr
                                            key={`${r.type}-${r.id}`}
                                            onClick={() => setSelectedTxn(r)}
                                            style={{
                                                cursor: 'pointer',
                                                background: isSelected ? '#1e3a5f' : 'transparent',
                                                borderBottom: '1px solid #1f2937',
                                            }}
                                        >
                                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                                <input type="radio" readOnly checked={!!isSelected} style={{ accentColor: '#3b82f6' }} />
                                            </td>
                                            <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: '#9ca3af' }}>{r.transaction_date || '—'}</td>
                                            <td style={{ padding: '6px 8px' }}>{r.description}</td>
                                            <td style={{ padding: '6px 8px', textAlign: 'right', color: r.amount < 0 ? '#f87171' : '#34d399' }}>
                                                {fmt(r.amount)}
                                            </td>
                                            <td style={{ padding: '6px 8px', color: '#9ca3af', whiteSpace: 'nowrap' }}>{r.account_label}</td>
                                            <td style={{ padding: '6px 8px', color: '#9ca3af' }}>{r.expense_category || '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: '10px 16px', borderTop: '1px solid #374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 13, color: '#9ca3af' }}>
                        {selectedTxn
                            ? <>Linking to: <strong style={{ color: '#e5e7eb' }}>{selectedTxn.description}</strong> ({fmt(selectedTxn.amount)})</>
                            : 'Click a row to select the primary transaction'}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
                        <button className="btn btn-success btn-sm" onClick={handleLink} disabled={saving || !selectedTxn}>
                            {saving ? 'Linking…' : '🔗 Link Reimbursement'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
